import React, { useState } from 'react';
import { ChevronLeft, MapPin, Bed, Bath, Maximize, Heart, Share2, Phone, Mail, MessageCircle, Calendar, DollarSign, Building2, CheckCircle, Navigation, Eye, Clock, User, Edit, Trash2 } from 'lucide-react';
interface PropertyDetailPageProps {
  propertyId: number;
  onBack: () => void;
}
export function PropertyDetailPage({
  propertyId,
  onBack
}: PropertyDetailPageProps) {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  // Mock property data
  const property = {
    id: propertyId,
    title: 'Luxury Villa with Pool',
    price: '8,500,000 EGP',
    pricePerSqm: '18,889 EGP/m²',
    location: 'New Cairo, 5th Settlement',
    fullAddress: 'Street 90, 5th Settlement, New Cairo, Cairo Governorate',
    type: 'Villa',
    transactionType: 'Sale',
    beds: 5,
    baths: 4,
    area: '450 sqm',
    landArea: '600 sqm',
    description: 'Stunning luxury villa featuring modern architecture and premium finishes. This spacious property offers an open-concept living area, gourmet kitchen, and expansive outdoor space with a private pool. Perfect for families seeking comfort and elegance in a prime location.',
    features: ['Private Swimming Pool', 'Garden', 'Garage for 2 Cars', "Maid's Room", 'Central Air Conditioning', 'Built-in Wardrobes', 'Fully Fitted Kitchen', 'Smart Home System', 'Security System', 'Balcony'],
    communityAmenities: ['24/7 Security', 'Gymnasium', 'Kids Play Area', 'Landscaped Gardens', 'Jogging Track', 'Community Center', 'Retail Shops', 'Covered Parking'],
    images: ['https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=800&q=80', 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80', 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80', 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?w=800&q=80', 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800&q=80'],
    propertyDetails: {
      propertyGroup: 'Residential',
      propertyStage: 'Ready to Move',
      finishingType: 'Fully Finished',
      view: 'Garden View',
      furnishing: 'Furnished',
      yearBuilt: '2022',
      floor: 'Ground + 1st Floor',
      compound: 'Palm Hills October'
    },
    paymentOptions: {
      cashPayment: true,
      installments: true,
      mortgage: true,
      downPayment: '20%',
      installmentYears: '5 years'
    },
    agent: {
      name: 'Ahmed Hassan',
      phone: '+20 123 456 7890',
      email: 'ahmed.hassan@realestate.com',
      company: 'Elite Properties',
      avatar: null
    },
    stats: {
      views: 1250,
      inquiries: 45,
      lastUpdated: '2 days ago',
      listedDate: 'Jan 15, 2024'
    }
  };
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: property.title,
        text: `Check out this property: ${property.title}`,
        url: window.location.href
      });
    } else {
      alert('Property link copied to clipboard!');
    }
  };
  const handleContact = () => {
    setShowContactModal(true);
  };
  const handleScheduleTour = () => {
    alert('Tour scheduling feature coming soon!');
  };
  return <div className="min-h-screen bg-neutral-bg pb-6 font-sans w-full" data-id="element-2967">
      <div className="w-full mx-auto bg-neutral-bg min-h-screen relative" data-id="element-2968">
        {/* Image Gallery */}
        <div className="relative" data-id="element-2969">
          <div className="relative h-80" data-id="element-2970">
            <img src={property.images[selectedImageIndex]} alt={property.title} className="w-full h-full object-cover" data-id="element-2971" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" data-id="element-2972"></div>

            {/* Back Button */}
            <button onClick={onBack} className="absolute top-4 left-4 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors z-10" data-id="element-2973">
              <ChevronLeft className="w-6 h-6 text-gray-700" data-id="element-2974" />
            </button>

            {/* Action Buttons */}
            <div className="absolute top-4 right-4 flex gap-2 z-10" data-id="element-2975">
              <button onClick={() => setIsFavorite(!isFavorite)} className="p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors" data-id="element-2976">
                <Heart className={`w-5 h-5 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-700'}`} data-id="element-2977" />
              </button>
              <button onClick={handleShare} className="p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors" data-id="element-2978">
                <Share2 className="w-5 h-5 text-gray-700" data-id="element-2979" />
              </button>
              <button className="p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors" data-id="element-2980">
                <Edit className="w-5 h-5 text-gray-700" data-id="element-2981" />
              </button>
            </div>

            {/* Property Type Badge */}
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2" data-id="element-2982">
              <div className="bg-secondary text-white text-xs font-bold px-4 py-2 rounded-full" data-id="element-2983">
                FOR {property.transactionType.toUpperCase()}
              </div>
            </div>

            {/* Price */}
            <div className="absolute bottom-4 left-4 right-4" data-id="element-2984">
              <div className="bg-black/70 backdrop-blur-sm text-white px-4 py-3 rounded-xl" data-id="element-2985">
                <p className="text-2xl font-bold mb-1" data-id="element-2986">{property.price}</p>
                <p className="text-sm text-white/80" data-id="element-2987">{property.pricePerSqm}</p>
              </div>
            </div>
          </div>

          {/* Image Thumbnails */}
          <div className="px-4 -mt-8 relative z-20" data-id="element-2988">
            <div className="flex gap-2 overflow-x-auto no-scrollbar" data-id="element-2989">
              {property.images.map((image, idx) => <button key={idx} onClick={() => setSelectedImageIndex(idx)} className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${selectedImageIndex === idx ? 'border-secondary scale-105' : 'border-white opacity-70 hover:opacity-100'}`} data-id="element-2990">
                  <img src={image} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" data-id="element-2991" />
                </button>)}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 mt-6 space-y-4" data-id="element-2992">
          {/* Title and Location */}
          <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-2993">
            <h1 className="text-2xl font-bold text-neutral-text mb-3" data-id="element-2994">
              {property.title}
            </h1>
            <div className="flex items-start gap-2 text-gray-600 mb-4" data-id="element-2995">
              <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" data-id="element-2996" />
              <div data-id="element-2997">
                <p className="font-medium" data-id="element-2998">{property.location}</p>
                <p className="text-sm text-gray-500" data-id="element-2999">{property.fullAddress}</p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-3 pt-4 border-t border-gray-100" data-id="element-3000">
              <div className="text-center" data-id="element-3001">
                <Bed className="w-5 h-5 text-gray-400 mx-auto mb-1" data-id="element-3002" />
                <p className="text-lg font-bold text-neutral-text" data-id="element-3003">
                  {property.beds}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3004">Beds</p>
              </div>
              <div className="text-center" data-id="element-3005">
                <Bath className="w-5 h-5 text-gray-400 mx-auto mb-1" data-id="element-3006" />
                <p className="text-lg font-bold text-neutral-text" data-id="element-3007">
                  {property.baths}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3008">Baths</p>
              </div>
              <div className="text-center" data-id="element-3009">
                <Maximize className="w-5 h-5 text-gray-400 mx-auto mb-1" data-id="element-3010" />
                <p className="text-lg font-bold text-neutral-text" data-id="element-3011">
                  {property.area}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3012">Built-up</p>
              </div>
              <div className="text-center" data-id="element-3013">
                <Building2 className="w-5 h-5 text-gray-400 mx-auto mb-1" data-id="element-3014" />
                <p className="text-lg font-bold text-neutral-text" data-id="element-3015">
                  {property.landArea}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3016">Land</p>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-3017">
            <div className="grid grid-cols-3 gap-4" data-id="element-3018">
              <div className="text-center" data-id="element-3019">
                <Eye className="w-5 h-5 text-blue-500 mx-auto mb-1" data-id="element-3020" />
                <p className="text-lg font-bold text-neutral-text" data-id="element-3021">
                  {property.stats.views}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3022">Views</p>
              </div>
              <div className="text-center" data-id="element-3023">
                <MessageCircle className="w-5 h-5 text-green-500 mx-auto mb-1" data-id="element-3024" />
                <p className="text-lg font-bold text-neutral-text" data-id="element-3025">
                  {property.stats.inquiries}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3026">Inquiries</p>
              </div>
              <div className="text-center" data-id="element-3027">
                <Clock className="w-5 h-5 text-purple-500 mx-auto mb-1" data-id="element-3028" />
                <p className="text-sm font-bold text-neutral-text" data-id="element-3029">
                  {property.stats.lastUpdated}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3030">Updated</p>
              </div>
            </div>
          </section>

          {/* Description */}
          <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-3031">
            <h2 className="text-base font-bold text-neutral-text mb-3" data-id="element-3032">
              Description
            </h2>
            <p className="text-sm text-gray-600 leading-relaxed" data-id="element-3033">
              {property.description}
            </p>
          </section>

          {/* Property Details */}
          <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-3034">
            <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-3035">
              Property Details
            </h2>
            <div className="grid grid-cols-2 gap-4" data-id="element-3036">
              <div data-id="element-3037">
                <p className="text-xs text-gray-500 mb-1" data-id="element-3038">Type</p>
                <p className="text-sm font-semibold text-neutral-text" data-id="element-3039">
                  {property.type}
                </p>
              </div>
              <div data-id="element-3040">
                <p className="text-xs text-gray-500 mb-1" data-id="element-3041">Property Group</p>
                <p className="text-sm font-semibold text-neutral-text" data-id="element-3042">
                  {property.propertyDetails.propertyGroup}
                </p>
              </div>
              <div data-id="element-3043">
                <p className="text-xs text-gray-500 mb-1" data-id="element-3044">Stage</p>
                <p className="text-sm font-semibold text-neutral-text" data-id="element-3045">
                  {property.propertyDetails.propertyStage}
                </p>
              </div>
              <div data-id="element-3046">
                <p className="text-xs text-gray-500 mb-1" data-id="element-3047">Finishing</p>
                <p className="text-sm font-semibold text-neutral-text" data-id="element-3048">
                  {property.propertyDetails.finishingType}
                </p>
              </div>
              <div data-id="element-3049">
                <p className="text-xs text-gray-500 mb-1" data-id="element-3050">View</p>
                <p className="text-sm font-semibold text-neutral-text" data-id="element-3051">
                  {property.propertyDetails.view}
                </p>
              </div>
              <div data-id="element-3052">
                <p className="text-xs text-gray-500 mb-1" data-id="element-3053">Furnishing</p>
                <p className="text-sm font-semibold text-neutral-text" data-id="element-3054">
                  {property.propertyDetails.furnishing}
                </p>
              </div>
              <div data-id="element-3055">
                <p className="text-xs text-gray-500 mb-1" data-id="element-3056">Year Built</p>
                <p className="text-sm font-semibold text-neutral-text" data-id="element-3057">
                  {property.propertyDetails.yearBuilt}
                </p>
              </div>
              <div data-id="element-3058">
                <p className="text-xs text-gray-500 mb-1" data-id="element-3059">Floor</p>
                <p className="text-sm font-semibold text-neutral-text" data-id="element-3060">
                  {property.propertyDetails.floor}
                </p>
              </div>
              <div className="col-span-2" data-id="element-3061">
                <p className="text-xs text-gray-500 mb-1" data-id="element-3062">Compound</p>
                <p className="text-sm font-semibold text-neutral-text" data-id="element-3063">
                  {property.propertyDetails.compound}
                </p>
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-3064">
            <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-3065">
              Property Features
            </h2>
            <div className="grid grid-cols-2 gap-3" data-id="element-3066">
              {property.features.map((feature, idx) => <div key={idx} className="flex items-center gap-2" data-id="element-3067">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-3068" />
                  <span className="text-sm text-gray-700" data-id="element-3069">{feature}</span>
                </div>)}
            </div>
          </section>

          {/* Community Amenities */}
          <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-3070">
            <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-3071">
              Community Amenities
            </h2>
            <div className="grid grid-cols-2 gap-3" data-id="element-3072">
              {property.communityAmenities.map((amenity, idx) => <div key={idx} className="flex items-center gap-2" data-id="element-3073">
                  <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" data-id="element-3074" />
                  <span className="text-sm text-gray-700" data-id="element-3075">{amenity}</span>
                </div>)}
            </div>
          </section>

          {/* Payment Options */}
          <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-3076">
            <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-3077">
              Payment Options
            </h2>
            <div className="space-y-3" data-id="element-3078">
              {property.paymentOptions.cashPayment && <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg" data-id="element-3079">
                  <CheckCircle className="w-5 h-5 text-green-600" data-id="element-3080" />
                  <span className="text-sm font-medium text-green-900" data-id="element-3081">
                    Cash Payment Available
                  </span>
                </div>}
              {property.paymentOptions.installments && <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg" data-id="element-3082">
                  <div className="flex items-center gap-3" data-id="element-3083">
                    <CheckCircle className="w-5 h-5 text-blue-600" data-id="element-3084" />
                    <span className="text-sm font-medium text-blue-900" data-id="element-3085">
                      Installment Plans
                    </span>
                  </div>
                  <div className="text-right" data-id="element-3086">
                    <p className="text-xs text-blue-600" data-id="element-3087">Down Payment</p>
                    <p className="text-sm font-bold text-blue-900" data-id="element-3088">
                      {property.paymentOptions.downPayment}
                    </p>
                  </div>
                </div>}
              {property.paymentOptions.mortgage && <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg" data-id="element-3089">
                  <CheckCircle className="w-5 h-5 text-purple-600" data-id="element-3090" />
                  <span className="text-sm font-medium text-purple-900" data-id="element-3091">
                    Mortgage Available
                  </span>
                </div>}
            </div>
          </section>

          {/* Agent Info */}
          <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-3092">
            <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-3093">
              Listed By
            </h2>
            <div className="flex items-center gap-4 mb-4" data-id="element-3094">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-secondary to-accent flex items-center justify-center text-white font-bold text-xl" data-id="element-3095">
                {property.agent.name.charAt(0)}
              </div>
              <div data-id="element-3096">
                <h3 className="font-bold text-neutral-text" data-id="element-3097">
                  {property.agent.name}
                </h3>
                <p className="text-sm text-gray-500" data-id="element-3098">
                  {property.agent.company}
                </p>
                <p className="text-xs text-gray-400 mt-1" data-id="element-3099">
                  Listed on {property.stats.listedDate}
                </p>
              </div>
            </div>
            <div className="space-y-2" data-id="element-3100">
              <a href={`tel:${property.agent.phone}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-3101">
                <Phone className="w-5 h-5 text-gray-600" data-id="element-3102" />
                <span className="text-sm font-medium text-gray-700" data-id="element-3103">
                  {property.agent.phone}
                </span>
              </a>
              <a href={`mailto:${property.agent.email}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-3104">
                <Mail className="w-5 h-5 text-gray-600" data-id="element-3105" />
                <span className="text-sm font-medium text-gray-700" data-id="element-3106">
                  {property.agent.email}
                </span>
              </a>
            </div>
          </section>

          {/* Map Placeholder */}
          <section className="bg-white rounded-xl p-5 shadow-sm border border-gray-100" data-id="element-3107">
            <h2 className="text-base font-bold text-neutral-text mb-4" data-id="element-3108">
              Location
            </h2>
            <div className="relative h-48 bg-gray-100 rounded-xl overflow-hidden" data-id="element-3109">
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-3110">
                <div className="text-center" data-id="element-3111">
                  <Navigation className="w-12 h-12 text-gray-400 mx-auto mb-2" data-id="element-3112" />
                  <p className="text-sm text-gray-500" data-id="element-3113">Map View</p>
                  <p className="text-xs text-gray-400" data-id="element-3114">{property.location}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3 sticky bottom-6 z-20" data-id="element-3115">
            <button onClick={handleScheduleTour} className="bg-white hover:bg-gray-50 text-secondary border-2 border-secondary font-bold py-4 rounded-xl transition-colors shadow-lg flex items-center justify-center gap-2" data-id="element-3116">
              <Calendar className="w-5 h-5" data-id="element-3117" />
              Schedule Tour
            </button>
            <button onClick={handleContact} className="bg-gradient-to-r from-secondary to-secondary-dark hover:from-secondary-dark hover:to-secondary text-white font-bold py-4 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2" data-id="element-3118">
              <MessageCircle className="w-5 h-5" data-id="element-3119" />
              Contact Agent
            </button>
          </div>
        </div>

        {/* Contact Modal */}
        {showContactModal && <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-id="element-3120">
            <div className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl p-6" data-id="element-3121">
              <div className="flex items-center justify-between mb-6" data-id="element-3122">
                <h2 className="text-xl font-bold text-neutral-text" data-id="element-3123">
                  Contact Agent
                </h2>
                <button onClick={() => setShowContactModal(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-neutral-bg rounded-full transition-colors" data-id="element-3124">
                  <ChevronLeft className="w-5 h-5" data-id="element-3125" />
                </button>
              </div>

              <div className="space-y-4" data-id="element-3126">
                <div data-id="element-3127">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-3128">
                    Your Name
                  </label>
                  <input type="text" placeholder="Enter your name" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-3129" />
                </div>

                <div data-id="element-3130">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-3131">
                    Phone Number
                  </label>
                  <input type="tel" placeholder="Enter your phone" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary" data-id="element-3132" />
                </div>

                <div data-id="element-3133">
                  <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-3134">
                    Message
                  </label>
                  <textarea rows={4} placeholder="I'm interested in this property..." className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary/20 focus:border-secondary resize-none" data-id="element-3135" />
                </div>

                <button className="w-full bg-secondary hover:bg-secondary-dark text-white font-bold py-4 rounded-xl transition-colors" data-id="element-3136">
                  Send Message
                </button>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}